"use client";

import React, { useState } from 'react';
import {
  Bell, Save, Loader2, Mail, MessageSquare, Smartphone, Monitor,
  Clock, AlertTriangle, ShieldAlert, Info, CheckCircle2, Check
} from 'lucide-react';
import { useAppToast } from '@/lib/use-app-toast';
import { auditLog } from '@/lib/supabase/audit-logger';

// ─── Types ────────────────────────────────────────────────────────────────────

type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info';
type Channel = 'email' | 'sms' | 'push' | 'in_app';

interface SeverityFilter {
  key: Severity;
  label: string;
  description: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: React.ReactNode;
  enabled: boolean;
}

interface DeliveryChannel {
  key: Channel;
  label: string;
  description: string;
  icon: React.ReactNode;
  enabled: boolean;
}

interface QuietHours {
  enabled: boolean;
  startHour: string;
  endHour: string;
  timezone: string;
  allowCritical: boolean;
  days: string[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function NotificationSettings() {
  const { success } = useAppToast();
  const [isSaving, setIsSaving] = useState(false);

  // Severity filters
  const [severityFilters, setSeverityFilters] = useState<SeverityFilter[]>([
    {
      key: 'critical',
      label: 'Critical',
      description: 'Immediate threats requiring urgent response',
      color: 'text-red-400',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
      icon: <ShieldAlert size={14} />,
      enabled: true,
    },
    {
      key: 'high',
      label: 'High',
      description: 'Significant threats with high impact potential',
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/30',
      icon: <AlertTriangle size={14} />,
      enabled: true,
    },
    {
      key: 'medium',
      label: 'Medium',
      description: 'Moderate threats requiring timely attention',
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
      icon: <AlertTriangle size={14} />,
      enabled: true,
    },
    {
      key: 'low',
      label: 'Low',
      description: 'Minor issues with limited impact',
      color: 'text-cyber-blue',
      bgColor: 'bg-cyber-blue/10',
      borderColor: 'border-cyber-blue/30',
      icon: <Info size={14} />,
      enabled: false,
    },
    {
      key: 'info',
      label: 'Informational',
      description: 'General system events and status updates',
      color: 'text-cyber-muted',
      bgColor: 'bg-white/5',
      borderColor: 'border-cyber-border',
      icon: <CheckCircle2 size={14} />,
      enabled: false,
    },
  ]);

  // Delivery channels
  const [channels, setChannels] = useState<DeliveryChannel[]>([
    {
      key: 'email',
      label: 'Email',
      description: 'Send alerts to your registered email address',
      icon: <Mail size={16} />,
      enabled: true,
    },
    {
      key: 'sms',
      label: 'SMS',
      description: 'Text message alerts to your mobile number',
      icon: <MessageSquare size={16} />,
      enabled: true,
    },
    {
      key: 'push',
      label: 'Push Notification',
      description: 'Browser and mobile push notifications',
      icon: <Smartphone size={16} />,
      enabled: false,
    },
    {
      key: 'in_app',
      label: 'In-App',
      description: 'Alerts displayed within the dashboard',
      icon: <Monitor size={16} />,
      enabled: true,
    },
  ]);

  // Quiet hours
  const [quietHours, setQuietHours] = useState<QuietHours>({
    enabled: false,
    startHour: '22:00',
    endHour: '07:00',
    timezone: 'Asia/Dhaka',
    allowCritical: true,
    days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  });

  const allDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const toggleSeverity = (key: Severity) => {
    setSeverityFilters(prev =>
      prev.map(s => s.key === key ? { ...s, enabled: !s.enabled } : s)
    );
  };

  const toggleChannel = (key: Channel) => {
    setChannels(prev =>
      prev.map(c => c.key === key ? { ...c, enabled: !c.enabled } : c)
    );
  };

  const toggleDay = (day: string) => {
    setQuietHours(prev => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day],
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise(r => setTimeout(r, 1000));
    await auditLog.preferencesUpdated();
    setIsSaving(false);
    success('Notification Settings Saved', 'Your alert preferences have been updated successfully.');
  };

  return (
    <div className="space-y-5">

      {/* ── Alert Severity Filters ─────────────────────────────────────────── */}
      <div className="cyber-card p-5">
        <div className="flex items-center gap-2 mb-1">
          <ShieldAlert size={15} className="text-red-400" />
          <h3 className="text-sm font-semibold text-white">Alert Severity Filters</h3>
        </div>
        <p className="text-xs text-cyber-muted mb-4">
          Choose which severity levels trigger notifications. Critical is always recommended.
        </p>

        <div className="space-y-2">
          {severityFilters.map((sev) => (
            <div
              key={sev.key}
              className={`flex items-center justify-between gap-4 px-4 py-3 rounded-xl border transition-all duration-150 ${
                sev.enabled ? `${sev.bgColor} ${sev.borderColor}` : 'bg-[#131620] border-cyber-border'
              }`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className={sev.enabled ? sev.color : 'text-cyber-muted'}>
                  {sev.icon}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${sev.enabled ? 'text-white' : 'text-cyber-muted'}`}>
                      {sev.label}
                    </span>
                    {sev.key === 'critical' && (
                      <span className="px-1.5 py-0.5 text-[9px] font-mono bg-red-500/20 text-red-400 border border-red-500/30 rounded uppercase tracking-wider">
                        Recommended
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-cyber-muted mt-0.5 truncate">{sev.description}</p>
                </div>
              </div>
              <button
                onClick={() => toggleSeverity(sev.key)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 shrink-0 ${
                  sev.enabled ? 'bg-cyber-blue' : 'bg-cyber-border'
                }`}
                aria-label={`Toggle ${sev.label} alerts`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                  sev.enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          ))}
        </div>

        {/* Active count badge */}
        <div className="mt-3 flex items-center gap-2">
          <span className="text-[11px] text-cyber-muted font-mono">
            {severityFilters.filter(s => s.enabled).length} of {severityFilters.length} severity levels active
          </span>
          <div className="flex gap-1">
            {severityFilters.map(s => (
              <span
                key={s.key}
                className={`w-2 h-2 rounded-full ${s.enabled ? s.color.replace('text-', 'bg-') : 'bg-cyber-border'}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Delivery Channels ─────────────────────────────────────────────── */}
      <div className="cyber-card p-5">
        <div className="flex items-center gap-2 mb-1">
          <Bell size={15} className="text-cyber-yellow" />
          <h3 className="text-sm font-semibold text-white">Delivery Channels</h3>
        </div>
        <p className="text-xs text-cyber-muted mb-4">
          Select how you want to receive alert notifications.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {channels.map((ch) => (
            <button
              key={ch.key}
              onClick={() => toggleChannel(ch.key)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all duration-150 ${
                ch.enabled
                  ? 'bg-cyber-blue/10 border-cyber-blue/35 shadow-[0_0_12px_rgba(0,163,255,0.07)]'
                  : 'bg-[#131620] border-cyber-border hover:border-cyber-blue/25'
              }`}
              aria-pressed={ch.enabled}
              aria-label={`Toggle ${ch.label} channel`}
            >
              <span className={`p-2 rounded-lg ${ch.enabled ? 'bg-cyber-blue/20 text-cyber-blue' : 'bg-white/5 text-cyber-muted'}`}>
                {ch.icon}
              </span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${ch.enabled ? 'text-white' : 'text-cyber-muted'}`}>
                  {ch.label}
                </p>
                <p className="text-[11px] text-cyber-muted mt-0.5 leading-tight">{ch.description}</p>
              </div>
              {ch.enabled && (
                <div className="w-5 h-5 rounded-full bg-cyber-blue flex items-center justify-center shrink-0">
                  <Check size={11} className="text-white" />
                </div>
              )}
            </button>
          ))}
        </div>

        {channels.filter(c => c.enabled).length === 0 && (
          <div className="mt-3 px-3 py-2 bg-yellow-500/10 border border-yellow-500/25 rounded-lg flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full shrink-0" />
            <p className="text-[11px] text-yellow-400 font-mono">
              No delivery channels selected — you will not receive any alerts
            </p>
          </div>
        )}
      </div>

      {/* ── Quiet Hours ───────────────────────────────────────────────────── */}
      <div className="cyber-card p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Clock size={15} className="text-cyber-purple" />
            <h3 className="text-sm font-semibold text-white">Quiet Hours</h3>
          </div>
          <button
            onClick={() => setQuietHours(prev => ({ ...prev, enabled: !prev.enabled }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
              quietHours.enabled ? 'bg-cyber-blue' : 'bg-cyber-border'
            }`}
            aria-label="Toggle quiet hours"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
              quietHours.enabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
        <p className="text-xs text-cyber-muted mb-4">
          Suppress non-critical notifications during specified hours.
        </p>

        <div className={`space-y-4 transition-opacity duration-200 ${quietHours.enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>

          {/* Time range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] text-cyber-muted font-mono uppercase tracking-wider mb-1.5">
                Start Time
              </label>
              <input
                type="time"
                value={quietHours.startHour}
                onChange={e => setQuietHours(prev => ({ ...prev, startHour: e.target.value }))}
                className="w-full bg-[#131620] border border-cyber-border rounded-lg px-3 py-2 text-sm text-cyber-text font-mono outline-none focus:border-cyber-blue/50 transition-colors"
              />
            </div>
            <div>
              <label className="block text-[11px] text-cyber-muted font-mono uppercase tracking-wider mb-1.5">
                End Time
              </label>
              <input
                type="time"
                value={quietHours.endHour}
                onChange={e => setQuietHours(prev => ({ ...prev, endHour: e.target.value }))}
                className="w-full bg-[#131620] border border-cyber-border rounded-lg px-3 py-2 text-sm text-cyber-text font-mono outline-none focus:border-cyber-blue/50 transition-colors"
              />
            </div>
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-[11px] text-cyber-muted font-mono uppercase tracking-wider mb-1.5">
              Timezone
            </label>
            <select
              value={quietHours.timezone}
              onChange={e => setQuietHours(prev => ({ ...prev, timezone: e.target.value }))}
              className="w-full bg-[#131620] border border-cyber-border rounded-lg px-3 py-2 text-sm text-cyber-text font-mono outline-none focus:border-cyber-blue/50 transition-colors cursor-pointer"
            >
              <option value="Asia/Dhaka">Asia/Dhaka (BDT, UTC+6)</option>
              <option value="UTC">UTC (Coordinated Universal Time)</option>
              <option value="America/New_York">America/New_York (EST/EDT)</option>
              <option value="Europe/London">Europe/London (GMT/BST)</option>
              <option value="Asia/Singapore">Asia/Singapore (SGT, UTC+8)</option>
              <option value="Asia/Tokyo">Asia/Tokyo (JST, UTC+9)</option>
            </select>
          </div>

          {/* Active days */}
          <div>
            <label className="block text-[11px] text-cyber-muted font-mono uppercase tracking-wider mb-2">
              Active Days
            </label>
            <div className="flex flex-wrap gap-2">
              {allDays.map(day => (
                <button
                  key={day}
                  onClick={() => toggleDay(day)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-mono font-medium transition-all duration-150 border ${
                    quietHours.days.includes(day)
                      ? 'bg-cyber-blue/15 border-cyber-blue/40 text-cyber-blue' :'bg-[#131620] border-cyber-border text-cyber-muted hover:border-cyber-blue/25'
                  }`}
                  aria-pressed={quietHours.days.includes(day)}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          {/* Allow critical override */}
          <div className="flex items-center justify-between px-4 py-3 bg-red-500/5 border border-red-500/20 rounded-xl">
            <div>
              <p className="text-sm font-medium text-white">Allow Critical Alerts</p>
              <p className="text-[11px] text-cyber-muted mt-0.5">
                Critical severity alerts bypass quiet hours and are always delivered
              </p>
            </div>
            <button
              onClick={() => setQuietHours(prev => ({ ...prev, allowCritical: !prev.allowCritical }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 shrink-0 ${
                quietHours.allowCritical ? 'bg-red-500' : 'bg-cyber-border'
              }`}
              aria-label="Toggle critical alert override"
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                quietHours.allowCritical ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* Summary */}
          {quietHours.enabled && (
            <div className="px-3 py-2.5 bg-cyber-blue/5 border border-cyber-blue/20 rounded-lg">
              <p className="text-[11px] text-cyber-blue font-mono">
                🌙 Quiet from <strong>{quietHours.startHour}</strong> to <strong>{quietHours.endHour}</strong> ({quietHours.timezone}) on{' '}
                {quietHours.days.length === 7 ? 'every day' : quietHours.days.join(', ')}.
                {quietHours.allowCritical && ' Critical alerts will still be delivered.'}
              </p>
            </div>
          )}
        </div>

        {!quietHours.enabled && (
          <div className="mt-2 px-3 py-2 bg-white/3 border border-cyber-border rounded-lg">
            <p className="text-[11px] text-cyber-muted font-mono">
              Quiet hours disabled — all alerts delivered at any time
            </p>
          </div>
        )}
      </div>

      {/* ── Save ──────────────────────────────────────────────────────────── */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={`
            flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium
            transition-all duration-150 active:scale-95
            ${isSaving
              ? 'bg-cyber-surface border border-cyber-border text-cyber-subtle cursor-not-allowed' :'bg-cyber-blue hover:bg-cyber-blue/80 text-white shadow-[0_0_15px_rgba(0,163,255,0.25)]'
            }
          `}
          style={{ minWidth: '160px' }}
        >
          {isSaving ? (
            <><Loader2 size={14} className="animate-spin" /><span>Saving...</span></>
          ) : (
            <><Save size={14} /><span>Save Notification Settings</span></>
          )}
        </button>
      </div>
    </div>
  );
}
